import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Grid, Type, Color } from '../../../app/StyleGuide';
import { SectionMediaItem, AddSectionMedia, SectionMediaPlaceholder } from './SessionTimelineContentElements';
import { Image } from '../../../components/Layout';
import { ImageButtonGroup, MediumOutlineButton } from '../../../components/Buttons';
import { RoundedBadge } from '../../../components/Badge';
import LoadingIndicator from '../../../components/LoadingIndicator';
import videoService from '../../../services/VideoService';
import { isAudioExtension } from '../../../utils/fileUtils';
import { formatMilliseconds } from '../../../utils/dateUtils';
import { Media } from '../models/timeline';

const MediaTypes = Object.freeze({
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
});

export const DurationBadge = styled(RoundedBadge)`
  background: ${Color.Gray._30};
  position: absolute;
  top: ${Grid._3};
  left: ${Grid._3};

  @media print {
    background-color: white !important;
  }
`;

const getThumbnailUrl = (media: Media) => media?.url?.thumbnail;

const SectionMediaThumbnail = ({
  media,
  viewOnly = false,
  title = '',
  onAddMedia,
  buttons,
  onClick,
  className = '',
}: {
  media: Media;
  viewOnly?: boolean;
  title?: string;
  onAddMedia?: () => void;
  buttons?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(getThumbnailUrl(media));
  const { id, _type, ext } = media ?? {};
  const hasMedia = !!id;
  const mediaType = _type === MediaTypes.VIDEO && isAudioExtension(ext) ? MediaTypes.AUDIO : _type;

  useEffect(() => {
    if (hasMedia && !thumbnailUrl && mediaType === MediaTypes.VIDEO) {
      videoService
        .getPoster(id)
        .then(poster => {
          if (!poster) return Promise.reject('Empty poster response. Possibly still encoding.');

          setThumbnailUrl(poster);
        })
        .catch(console.error);
    }
  }, []);

  useEffect(() => {
    const thumbnail = getThumbnailUrl(media);
    if (thumbnail !== thumbnailUrl) setThumbnailUrl(thumbnail);
  }, [media?.url?.thumbnail]);

  if (mediaType === MediaTypes.AUDIO)
    return (
      <SectionMediaItem onClick={onClick} data-tracking-id={media.name || id} className={className}>
        <SectionMediaPlaceholder>
          {!!media.duration && <DurationBadge>{formatMilliseconds(media.duration)}</DurationBadge>}
          <i className="fas fa-music" style={{ fontSize: Type.Scale._7 }}></i>
          <ImageButtonGroup>{buttons}</ImageButtonGroup>
        </SectionMediaPlaceholder>
      </SectionMediaItem>
    );

  if (hasMedia && thumbnailUrl)
    return (
      <SectionMediaItem onClick={onClick} data-tracking-id={media.name || id} className={className}>
        {!!media.duration && <DurationBadge>{formatMilliseconds(media.duration)}</DurationBadge>}
        <Image src={thumbnailUrl} alt={title} width="100%" />
        <ImageButtonGroup>{buttons}</ImageButtonGroup>
      </SectionMediaItem>
    );

  if (hasMedia)
    return (
      <SectionMediaItem className={className}>
        <AddSectionMedia>
          <LoadingIndicator />
        </AddSectionMedia>
      </SectionMediaItem>
    );

  if (!viewOnly)
    return (
      <SectionMediaItem className={className}>
        <AddSectionMedia>
          <MediumOutlineButton data-qa-hook="addSectionMedia" onClick={onAddMedia}>
            Add Media
          </MediumOutlineButton>
        </AddSectionMedia>
      </SectionMediaItem>
    );

  return null;
};

export default SectionMediaThumbnail;
